import {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  getSelectedWords,
  getTags,
  getWords,
  TagResponse,
} from '@/api/services/wordset';
import {
  BASIC_STATE,
  CHANGE_PAGE,
  OUTER_STATE,
  PHONEME_STATE,
  PHONEME_ROW_STATE,
  PHONEME_ALL_STATE,
  RESET_STATE,
} from './constants';
import { logError } from '@/utils/logHandling';
import {
  StateType,
  ActionType,
  PARTS_OF_SPEECH_KEY,
  WordSetContextType,
} from './type';
import { getTemplate } from '@/api/services/templates';
import { Word } from '@/stores/store.types';
import { Template } from '@/components/Template/types';
import { ScrollView } from 'react-native';

type WordState = {
  state: StateType;
  apiKeyName?: 'wordsets';
  changeAllPhenomenonState?: (
    name: 'initial' | 'medial' | 'final',
    values: string[],
    isRemove: boolean,
  ) => void;
  changeBasicState?: (
    name:
      | 'Silbenanzahl'
      | 'Silbenstruktur'
      | 'Wortakzent'
      | 'Wortfrequenz'
      | 'Genus'
      | 'Numerus',
    value: unknown,
    isRemove: boolean,
  ) => void;
  changeLimitModal?: () => void;
  changeOuterState?: (name: keyof StateType, value: unknown) => void;
  changePage?: (value: number, arrowName?: null) => void;
  changePhenomenonRowState?: (
    name: 'initial' | 'medial' | 'final',
    values: string[],
    isRemove: boolean,
  ) => void;
  changePhenomenonState?: (
    name: 'initial' | 'medial' | 'final',
    value: string,
    isRemove: boolean,
  ) => void;
  changeSelectedData?: (newSelectedData: any) => void;
  data?: {
    totalCount: number;
    words: Word[]; // TODO
  };
  dataKeyName?: 'words';
  dynamicName?: 'wordset';
  editData?: any | null;
  limitModal?: boolean;
  patientId?: string;
  refetchWords?: () => void;
  refreshWordsSet?: boolean;
  retinaImage?: string;
  scrollViewRef?: React.RefObject<ScrollView | null>;
  selectedWords?: Word[];
  setId?: string;
  tags?: string[];
  template?: Template | null;
  toggleRefreshWordSetTrue?: () => void;
};

const DATA_DEFAULT_VALUE = {
  words: [],
  totalCount: 0,
};

const DEFAULT_VALUE: StateType = {
  term: '',
  termType: 'word',
  searchType: 'basic',
  searchWordsPerPage: 30,
  page: 1,
  partsOfSpeech: 'nomen',
  phonemes: {
    initialSelected: [
      'a',
      'e',
      'i',
      'o',
      'u',
      'ä',
      'ö',
      'ü',
      'au',
      'ei',
      'eu',
      'p',
      'b',
      't',
      'd',
      'k',
      'g',
      'f',
      'w',
      's (stl)',
      's (sth)',
      'sch',
      'h',
      'ch1',
      'pf',
      'ts',
      'm',
      'n',
      'r',
      'l',
      'j',
    ],
    initialUnselected: [],
    medialSelected: [
      'a',
      'e',
      'i',
      'o',
      'u',
      'ä',
      'ö',
      'ü',
      'schwa',
      'au',
      'ei',
      'eu',
      'p',
      'b',
      't',
      'd',
      'k',
      'g',
      'f',
      'w',
      's (stl)',
      's (sth)',
      'sch',
      'h',
      'ch1',
      'ch2',
      'pf',
      'ts',
      'm',
      'n',
      'ng',
      'r',
      'l',
      'j',
    ],
    medialUnselected: [],
    finalSelected: [
      'a',
      'e',
      'i',
      'o',
      'u',
      'ä',
      'ö',
      'ü',
      'schwa',
      'au',
      'ei',
      'eu',
      'p',
      't',
      'k',
      'f',
      's (stl)',
      'sch',
      'ch1',
      'ch2',
      'pf',
      'ts',
      'm',
      'n',
      'ng',
      'l',
    ],
    finalUnselected: [],
  },
  basic: {
    Silbenanzahl: [1, 2, 3, 4, 5],
    Silbenstruktur: ['einfach', 'komplex'],
    Wortakzent: [1, 2, 3, 4, 5],
    Wortfrequenz: ['hoch', 'niedrig'],
    Genus: ['männlich', 'weiblich', 'neutrum'],
    Numerus: [0, 1],
  },
};

export const WordsetContext = createContext<WordState>({
  state: DEFAULT_VALUE,
});

const reducer = (state: StateType, action: ActionType): StateType => {
  const { type, payload } = action;
  switch (type) {
    case RESET_STATE: {
      return {
        ...DEFAULT_VALUE,
        termType: state.termType,
        partsOfSpeech: state.partsOfSpeech,
      };
    }
    case OUTER_STATE: {
      const { name, value } = payload;
      const copyOfBasic = { ...state.basic };

      if (['verb', 'adjektiv', 'zahlenwort'].includes(value as string)) {
        copyOfBasic.Genus = DEFAULT_VALUE.basic.Genus;
        copyOfBasic.Numerus = DEFAULT_VALUE.basic.Numerus;
      } else if (value === 'floskel') {
        copyOfBasic.Genus = DEFAULT_VALUE.basic.Genus;
        copyOfBasic.Numerus = DEFAULT_VALUE.basic.Numerus;
        copyOfBasic.Wortakzent = DEFAULT_VALUE.basic.Wortakzent;
        copyOfBasic.Wortfrequenz = DEFAULT_VALUE.basic.Wortfrequenz;
      }

      return {
        ...state,
        page: 1,
        [name]: value,
        basic: copyOfBasic,
      };
    }
    case CHANGE_PAGE: {
      const { value, arrowName } = payload;
      if (arrowName === null) {
        return {
          ...state,
          page: value,
        };
      } else {
        if (arrowName === 'right') {
          return {
            ...state,
            page: state.page + 1,
          };
        } else {
          return {
            ...state,
            page: state.page - 1,
          };
        }
      }
    }
    case BASIC_STATE: {
      const { name, value, isRemove } = payload;
      const pickedArray = state.basic[name];
      let newPickedArray = null;
      if (isRemove) {
        newPickedArray = pickedArray.filter((arrVal) => {
          return arrVal !== value;
        });
      } else {
        newPickedArray = [...pickedArray, value];
      }
      return {
        ...state,
        page: 1,
        basic: {
          ...state.basic,
          [name]: newPickedArray,
        },
      };
    }
    case PHONEME_ROW_STATE: {
      const { name, values, isRemove } = payload;
      let newSelectedArray, newUnselectedArray;
      if (isRemove) {
        newUnselectedArray = [...state.phonemes[name + 'Unselected']];
        newSelectedArray = state.phonemes[name + 'Selected'].filter(
          (eachVal: string) => {
            if (values.includes(eachVal)) {
              newUnselectedArray.push(eachVal);
            }
            return !values.includes(eachVal);
          },
        );
      } else {
        newSelectedArray = [...state.phonemes[name + 'Selected']];
        newUnselectedArray = state.phonemes[name + 'Unselected'].filter(
          (eachVal: string) => {
            if (values.includes(eachVal)) {
              newSelectedArray.push(eachVal);
            }
            return !values.includes(eachVal);
          },
        );
      }
      return {
        ...state,
        page: 1,
        phonemes: {
          ...state.phonemes,
          [name + 'Selected']: newSelectedArray,
          [name + 'Unselected']: newUnselectedArray,
        },
      };
    }
    case PHONEME_STATE: {
      const { name, value, isRemove } = payload;
      let newSelectedArray, newUnselectedArray;
      if (isRemove) {
        newSelectedArray = state.phonemes[name + 'Selected'].filter(
          (arrVal: string) => {
            if (arrVal === value) {
              newUnselectedArray = [...state.phonemes[name + 'Unselected']];
              newUnselectedArray.push(arrVal);
            }
            return arrVal !== value;
          },
        );
      } else {
        newUnselectedArray = state.phonemes[name + 'Unselected'].filter(
          (arrVal: string) => {
            if (arrVal === value) {
              newSelectedArray = [...state.phonemes[name + 'Selected']];
              newSelectedArray.push(arrVal);
            }
            return arrVal !== value;
          },
        );
      }

      return {
        ...state,
        page: 1,
        phonemes: {
          ...state.phonemes,
          [name + 'Selected']: newSelectedArray,
          [name + 'Unselected']: newUnselectedArray,
        },
      };
    }
    case PHONEME_ALL_STATE: {
      const { name, values, isRemove } = payload;
      let newSelectedArray: string[], newUnselectedArray: string[];
      if (isRemove) {
        newSelectedArray = [];
        newUnselectedArray = values;
      } else {
        newSelectedArray = values;
        newUnselectedArray = [];
      }

      return {
        ...state,
        page: 1,
        phonemes: {
          ...state.phonemes,
          [name + 'Selected']: newSelectedArray,
          [name + 'Unselected']: newUnselectedArray,
        },
      };
    }
    default: {
      return state;
    }
  }
};

interface WordsetContextProviderProps {
  children: React.ReactNode;
  patientId?: string;
  setId?: string;
  apiName: string;
  fromTemplate?: string;
}

export function WordsetContextProvider({
  children,
  patientId,
  setId,
  apiName,
  fromTemplate,
}: WordsetContextProviderProps) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_VALUE);
  const [data, setData] = useState(DATA_DEFAULT_VALUE);
  const [template, setTemplate] = useState<Template | null>(null);

  // on pagination press, we want to scroll up in the sets
  const scrollViewRef = useRef<ScrollView | null>(null);

  const [limitModal, setLimitModal] = useState(false);
  const [selectedData, setSelectedData] = useState<Word[]>([]);
  const [tags, setTags] = useState<Omit<
    TagResponse,
    'sentences' | 'texts'
  > | null>(null);
  const componentMounted = useRef(false);
  const requestRef = useRef(null);
  const editData = useRef<WordSetContextType[]>(null);
  const [refreshWordsSet, setRefreshWordsSet] = useState(false);
  const [refreshUnlock, setRefreshUnlock] = useState(false);

  const toggleRefreshWordSetTrue = useCallback(() => {
    setRefreshUnlock(true);
    setRefreshWordsSet((prev) => !prev);
  }, []);

  const changeOuterState = useCallback(
    (name: keyof StateType, value: unknown) => {
      dispatch({
        type: OUTER_STATE,
        payload: { name, value },
      });
    },
    [],
  );

  const changePage = useCallback((value: number, arrowName = null) => {
    dispatch({
      type: CHANGE_PAGE,
      payload: { value, arrowName },
    });
    if (scrollViewRef.current && value) {
      scrollViewRef.current.scrollTo({ y: 400, animated: true });
    }
  }, []);

  const changeBasicState = useCallback(
    (name: keyof StateType['basic'], value: unknown, isRemove: boolean) => {
      dispatch({
        type: BASIC_STATE,
        payload: { name, value, isRemove },
      });
    },
    [],
  );

  const changePhenomenonState = useCallback(
    (
      name: 'initial' | 'medial' | 'final',
      value: string,
      isRemove: boolean,
    ) => {
      dispatch({
        type: PHONEME_STATE,
        payload: { name, value, isRemove },
      });
    },
    [],
  );

  const changePhenomenonRowState = useCallback(
    (
      name: 'initial' | 'medial' | 'final',
      values: string[],
      isRemove: boolean,
    ) => {
      dispatch({
        type: PHONEME_ROW_STATE,
        payload: { name, values, isRemove },
      });
    },
    [],
  );

  const changeAllPhenomenonState = useCallback(
    (
      name: 'initial' | 'medial' | 'final',
      values: string[],
      isRemove: boolean,
    ) => {
      dispatch({
        type: PHONEME_ALL_STATE,
        payload: { name, values, isRemove },
      });
    },
    [],
  );

  const changeLimitModal = useCallback(() => {
    setLimitModal((prevState) => !prevState);
  }, []);

  const changeSelectedData = useCallback((newSelectedData: Word[]) => {
    setSelectedData(newSelectedData);
  }, []);

  const handleFetchWords = useCallback(async (object: StateType) => {
    try {
      let payload;
      if (object?.termType === 'word' || object?.termType === 'tag') {
        payload = { ...object, term: object?.term.trim() };
      } else {
        payload = { ...object };
      }
      // ? In case no word frequency is selected nothing should be displayed
      if (payload.basic.Wortfrequenz.length === 0) {
        return setData(DATA_DEFAULT_VALUE);
      }
      const { data } = await getWords(payload);
      setData(data);
    } catch (error) {
      logError('Could not fetch words', error);
    }
  }, []);

  const handleFetchTags = useCallback(async () => {
    try {
      const { data } = await getTags();
      if (data) {
        const { nouns, verbs, adjectives, numberWords, fillerWords } = data;
        setTags({ nouns, verbs, adjectives, numberWords, fillerWords });
      }
    } catch (error) {
      logError('Could not fetch tags', error);
    }
  }, []);

  const handleFetchSelectedWords = useCallback(
    async (apiName: string, patientId: string, setId: string) => {
      try {
        const { data } = await getSelectedWords(apiName, patientId, setId);
        editData.current = data;
        if (data == null) {
          return;
        }
        const { wordsetItems } = data;
        const wordsetItemsMapped = wordsetItems
          .filter((eachWordset) => {
            return eachWordset.active;
          })
          .map((eachWordset) => {
            return {
              _id: eachWordset._id,
              ...eachWordset.word,
            };
          });
        setSelectedData(wordsetItemsMapped);
      } catch (error) {
        logError('Could not fetch selected words', error);
      }
    },
    [],
  );

  const refetchWords = useCallback(() => {
    handleFetchWords(state);
  }, [state]);

  const isCheckedAll = () => {
    if (
      state.phonemes.initialUnselected.length === 0 &&
      state.phonemes.medialUnselected.length === 0 &&
      state.phonemes.finalUnselected.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (
      state.basic.Silbenanzahl.length === 5 &&
      state.basic.Silbenstruktur.length === 2 &&
      state.basic.Wortakzent.length === 5 &&
      state.basic.Wortfrequenz.length === 2 &&
      state.basic.Genus.length === 3 &&
      state.basic.Numerus.length === 2 &&
      isCheckedAll()
    ) {
      dispatch({
        type: OUTER_STATE,
        payload: {
          name: 'searchType',
          value: 'basic',
        },
      });
    } else {
      dispatch({
        type: OUTER_STATE,
        payload: {
          name: 'searchType',
          value: 'complex',
        },
      });
    }
  }, [
    state.basic.Silbenanzahl.length,
    state.basic.Silbenstruktur.length,
    state.basic.Wortakzent.length,
    state.basic.Wortfrequenz.length,
    state.basic.Genus.length,
    state.basic.Numerus.length,
    isCheckedAll(),
  ]);

  useEffect(() => {
    if (componentMounted.current && state.page > 0) {
      clearTimeout(requestRef.current);
      requestRef.current = setTimeout(() => {
        handleFetchWords(state);
      }, 500);
    }
  }, [state.term]);

  useEffect(() => {
    if (componentMounted.current && state.page > 0) {
      const handler = setTimeout(() => {
        handleFetchWords(state);
        // to avoid multiple calls which is the case when "basic" changes
      }, 200);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [
    state.basic,
    state.page,
    state.partsOfSpeech,
    state.termType,
    state.searchType,
  ]);

  useEffect(() => {
    const fetchWords = async () => {
      if (!setId) {
        handleFetchTags();
        if (fromTemplate) {
          const template = await getTemplate(fromTemplate);
          setTemplate(template);

          const wordsetItemsMapped = (template.items ?? [])
            .filter((eachWordset) => {
              return eachWordset.active;
            })
            .map((eachWordset) => {
              return eachWordset.word;
            })
            .filter((word): word is Word => word !== undefined);
          setSelectedData(wordsetItemsMapped);
          handleFetchWords(state);
        } else {
          handleFetchWords(state);
        }
      } else {
        try {
          handleFetchTags();
          handleFetchWords(state);
          handleFetchSelectedWords(apiName, patientId, setId);
        } catch (error) {
          logError('Could not fetch', error);
        }
      }
      componentMounted.current = true;
    };
    fetchWords();
  }, [fromTemplate]);

  useEffect(() => {
    // block the call if the user presses refresh without changing the toggle options
    // and if it needs to refresh, bring the default state but "Wörter suchen" and Wortart options are based on what's clicked on
    if (refreshUnlock) {
      const defaultState = {
        ...DEFAULT_VALUE,
        termType: state.termType,
        partsOfSpeech: state.partsOfSpeech,
      };
      const keys1 = Object.keys(defaultState);
      const keys2 = Object.keys(state);
      if (
        keys1.length !== keys2.length ||
        !keys1.every((key) => keys2.includes(key))
      ) {
        return;
      }
      const value = keys1.every((key) => defaultState[key] === state[key]);
      if (value) {
        return;
      }
      dispatch({
        type: RESET_STATE,
        payload: {},
      });
      handleFetchWords(defaultState);
    }
  }, [refreshWordsSet, refreshUnlock]);

  const contextState: WordState = {
    state,
    data,
    selectedWords: selectedData,
    patientId,
    setId,
    editData: editData.current,
    limitModal,
    changeLimitModal,
    changeOuterState,
    changePage,
    changeBasicState,
    changePhenomenonState,
    changePhenomenonRowState,
    changeAllPhenomenonState,
    changeSelectedData,
    refetchWords,
    refreshWordsSet,
    toggleRefreshWordSetTrue,
    scrollViewRef,
    tags:
      tags?.[
        PARTS_OF_SPEECH_KEY[state.partsOfSpeech] as keyof Omit<
          TagResponse,
          'sentences' | 'texts'
        >
      ]?.map((item) => {
        return item.displayHierarchy;
      }) ?? [],
    template,
  };

  return (
    <WordsetContext.Provider value={contextState}>
      {children}
    </WordsetContext.Provider>
  );
}
